import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Cloud, Check, Loader2, LogOut, AlertCircle, ExternalLink } from "lucide-react";

const DriveConnectCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/drive/status");
      setStatus(res.data);
    } catch (error) {
      console.error("Failed to load Drive status:", error);
      setStatus({ available: false, connected: false });
    }
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("drive")) {
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(load, 800);
    }
  }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const res = await api.get("/drive/connect");
      window.location.href = res.data.authorization_url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to start Google Drive connection");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? Future uploads will no longer sync. Existing files in Drive will remain.")) return;
    setBusy(true);
    try {
      await api.delete("/drive/disconnect");
      await load();
    } finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <div className="bg-white rounded-2xl soft-shadow p-6 border border-cream-200">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-coral-300/15 text-coral-300 flex items-center justify-center shrink-0">
          <Cloud className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif italic text-navy-700 text-xl">Google Drive sync</h3>
            {status.connected && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                <Check className="w-3 h-3" /> Connected
              </span>
            )}
          </div>

          {!status.available && (
            <div className="mt-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{status.reason || "Drive integration is not configured on the server yet."}</span>
            </div>
          )}

          {status.available && !status.connected && (
            <>
              <p className="text-sm text-navy-700/70 mt-2">
                Connect your Google Drive and every photo or video your guests upload will save automatically to a private folder in your Drive — in real time.
              </p>
              <ul className="mt-3 space-y-1.5 text-sm text-navy-700/75">
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-coral-300" /> Per-event folder under <strong>GalerieCop</strong></li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-coral-300" /> Uploader name prefixed for clarity</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-coral-300" /> Only files we create — we don&apos;t read your existing Drive</li>
              </ul>
              <button
                onClick={connect}
                disabled={busy}
                className="mt-5 inline-flex items-center gap-2 bg-coral-300 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-coral-400 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
                {busy ? "Redirecting..." : "Connect Google Drive"}
              </button>
            </>
          )}

          {status.connected && (
            <>
              <p className="text-sm text-navy-700/70 mt-2">
                Connected as <strong>{status.email || "your Google account"}</strong>. Every new upload from your guests is being saved to your Drive automatically.
              </p>
              {status.connected_at && (
                <p className="text-xs text-navy-700/50 mt-1">Connected {new Date(status.connected_at).toLocaleString()}</p>
              )}
              <div className="mt-5 flex items-center gap-3">
                <a href="https://drive.google.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-coral-300 hover:text-coral-400 text-sm font-semibold">
                  Open Drive <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button
                  onClick={disconnect}
                  disabled={busy}
                  className="inline-flex items-center gap-2 border border-red-200 text-red-500 px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-red-50 disabled:opacity-60"
                >
                  <LogOut className="w-3.5 h-3.5" /> Disconnect
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriveConnectCard;
